import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Package, CreditCard, Truck, CheckCircle2, Clock, Search,
  Plus, Filter, ChevronRight
} from 'lucide-react';

const OrdersTab = () => {
  const [statusFilter, setStatusFilter] = useState('all');

  const orders = [
    { id: 'SO-2024-0892', customer: 'Al Rajhi Trading', total: 125000, status: 'Dispatched', items: 12, date: '2024-03-10' },
    { id: 'SO-2024-0891', customer: 'Gulf Distributors', total: 89500, status: 'Packed', items: 8, date: '2024-03-10' },
    { id: 'SO-2024-0890', customer: 'Saudi Foods Co', total: 234000, status: 'Processing', items: 24, date: '2024-03-09' },
    { id: 'SO-2024-0889', customer: 'Metro Retail', total: 67200, status: 'Delivered', items: 6, date: '2024-03-08' },
    { id: 'QT-2024-0441', customer: 'Desert Goods LLC', total: 45000, status: 'Draft', items: 5, date: '2024-03-10' },
  ];

  const statusSteps = ['Draft', 'Processing', 'Packed', 'Dispatched', 'Delivered'];

  const getStatusColor = (status) => {
    const map = { Draft: 'bg-gray-100 text-gray-700', Processing: 'bg-blue-100 text-blue-700', Packed: 'bg-amber-100 text-amber-700', Dispatched: 'bg-indigo-100 text-indigo-700', Delivered: 'bg-green-100 text-green-700' };
    return map[status] || map.Draft;
  };

  const filtered = statusFilter === 'all' ? orders : orders.filter((o) => o.status === statusFilter);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-900">Orders</h2>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All statuses</option>
            {statusSteps.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
            <Plus className="w-4 h-4" />
            New order
          </button>
        </div>
      </div>

      {/* Quote to order workflow */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote → Fulfilled order</h3>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-teal-600" />
            </div>
            <span className="text-gray-700">Draft order</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Package className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-gray-700">Stock check & soft allocate</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-amber-600" />
            </div>
            <span className="text-gray-700">Credit check</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Truck className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-gray-700">SO → Warehouse pick & pack</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Track</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{order.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{order.customer}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">SAR {order.total.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{order.items}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{order.date}</td>
                  <td className="px-6 py-4 text-right">
                    <button className="text-teal-600 hover:text-teal-800 p-1" title="Track order">
                      <Truck className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrdersTab;
